import { Stack } from '@startuptools/ui/Stack';
import { FormControl } from '@startuptools/ui/joy/FormControl';
import { Box } from '@startuptools/ui/joy/Box';
import { Switch } from '@startuptools/ui/joy/Switch';
import { Controller, UseFormReturn } from 'react-hook-form';
import { ExtractKeysOfValueType, FormData } from '../types';
import { Trans } from '@lingui/react';
import { Typography } from '@startuptools/ui/joy/Typography';
import { RichTextInput } from '@startuptools/ui/RichTextInput';
import { Divider } from '@startuptools/ui/joy/Divider';

interface ToggleTextFieldProps {
  label: React.ReactNode;
  form: UseFormReturn<FormData>;
  booleanName: NonNullable<ExtractKeysOfValueType<FormData, boolean | null | undefined>>;
  stringName: NonNullable<ExtractKeysOfValueType<FormData, string | null | undefined>>;
  changeName: NonNullable<ExtractKeysOfValueType<FormData, boolean | null | undefined>>;
  defaultValue: string;
}

export const ToggleTextField = ({
  changeName,
  booleanName,
  stringName,
  label,
  form,
  defaultValue,
}: ToggleTextFieldProps) => (
  <Stack gap={1}>
    <Box>
      <Typography fontWeight="bold" level="h4">
        {label}
      </Typography>
    </Box>
    <Controller
      control={form.control}
      name={changeName}
      render={({ field }) => (
        <Box>
          <Switch
            checked={field.value ?? false}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => field.onChange(event.target.checked)}
            color={field.value ? 'primary' : 'neutral'}
            variant={field.value ? 'solid' : 'outlined'}
            endDecorator={<Trans id="Mark as change" />}
            slotProps={{
              endDecorator: {
                sx: {
                  minWidth: 24,
                },
              },
            }}
          />
        </Box>
      )}
    />
    <Controller
      control={form.control}
      name={booleanName}
      render={({ field }) => (
        <Box>
          <Switch
            checked={field.value ?? false}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              if (event.target.checked) {
                form.setValue(stringName, defaultValue);
              } else {
                form.setValue(stringName, null);
              }
              field.onChange(event.target.checked);
            }}
            color={field.value ? 'primary' : 'neutral'}
            variant={field.value ? 'solid' : 'outlined'}
            endDecorator={<Trans id="Include" />}
            slotProps={{
              endDecorator: {
                sx: {
                  minWidth: 24,
                },
              },
            }}
          />
        </Box>
      )}
    />

    {form.watch(booleanName) && (
      <Controller
        control={form.control}
        name={stringName}
        rules={{
          required: true,
          validate: value => {
            if (!value) return false;
            const div = document.createElement('div');
            div.innerHTML = value;
            return (div.textContent?.trim()?.length || 0) > 0;
          },
        }}
        render={({ field, formState }) => (
          <FormControl>
            <RichTextInput
              style={{ border: formState.errors[stringName] ? '1px solid red' : undefined }}
              value={field.value ?? ''}
              onChange={value => field.onChange(value)}
            />
          </FormControl>
        )}
      />
    )}
    <Divider sx={{ my: 2 }} />
  </Stack>
);
